import { useEffect, useState } from "react";
import api from "@/api";
import { useNoctua } from "./NoctuaContext";
import { Trophy, Zap, Users, Heart, MapIcon, Clock, AlertCircle, Download } from "lucide-react";

export default function InteligenciaPage() {
  const { partidos, selectedState, scopeFilter } = useNoctua();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isMunicipalScope = scopeFilter?.scope_type === "municipal" && scopeFilter?.municipio_clave;
  const isDistrictScope = scopeFilter?.scope_type === "distrital_federal" || scopeFilter?.scope_type === "distrital_local";
  const districtLoaded = isDistrictScope && scopeFilter?.district_mapping_loaded && (scopeFilter.district_municipios_claves?.length > 0);
  const districtMunisKey = districtLoaded ? scopeFilter.district_municipios_claves.join(",") : "";

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ estado_clave: String(selectedState) });
    if (isMunicipalScope) params.set("municipio_clave", String(scopeFilter.municipio_clave));
    else if (districtLoaded) params.set("municipios_claves", districtMunisKey);
    api.get(`/noctua/inteligencia/resumen?${params.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [selectedState, isMunicipalScope, scopeFilter?.municipio_clave, districtLoaded, districtMunisKey]);

  const partyColor = (k) => partidos.find((p) => p.clave === k)?.color || "#64748B";

  if (loading) return <div className="text-slate-400 p-8">Cargando inteligencia…</div>;
  if (!data) return <div className="text-slate-400 p-8">Sin datos</div>;

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="noctua-inteligencia-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Inteligencia operativa</div>
        <h1 className="mt-2 font-display font-black text-3xl sm:text-4xl text-white">Panel de Inteligencia</h1>
        <p className="text-sm text-slate-400 mt-1">
          KPIs en tiempo real · alertas Noctua · partido: <strong className="text-white">{data.partido_cliente || "—"}</strong>
        </p>
      </div>

      {isMunicipalScope && (
        <div className="noctua-card p-3 border-l-4 border-l-emerald-500 flex items-center gap-2 text-sm text-slate-200" data-testid="int-municipal-banner">
          <MapIcon className="w-4 h-4 text-emerald-400" />
          KPIs restringidos al municipio{" "}
          <strong className="text-emerald-300 ml-1">{scopeFilter?.municipio_nombre || `#${scopeFilter?.municipio_clave}`}</strong>
        </div>
      )}

      {isDistrictScope && !districtLoaded && (
        <div className="noctua-card p-3 border-l-4 border-l-amber-500 flex items-start gap-2 text-sm text-slate-200" data-testid="int-distrital-banner">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <strong className="text-amber-400">Cartografía distrital pendiente.</strong>{" "}
            Estás en el distrito {scopeFilter?.scope_type === "distrital_federal" ? "federal" : "local"} #{scopeFilter?.distrito_clave} de {scopeFilter?.entidad_nombre}. Los KPIs se muestran para todo el estado hasta que el Super Admin cargue el mapping distrito→municipios en Datos.
          </div>
        </div>
      )}

      {isDistrictScope && districtLoaded && (
        <div className="noctua-card p-3 border-l-4 border-l-emerald-500 flex items-center gap-2 text-sm text-slate-200" data-testid="int-distrital-loaded-banner">
          <MapIcon className="w-4 h-4 text-emerald-400" />
          KPIs restringidos al {scopeFilter?.scope_type === "distrital_federal" ? "Distrito Federal" : "Distrito Local"}{" "}
          <strong className="text-emerald-300 ml-1">#{scopeFilter?.distrito_clave}</strong>
          <span className="text-slate-400 mx-1">·</span>
          <span className="text-slate-300">{scopeFilter.district_municipios_claves.length} municipios</span>
        </div>
      )}

      {/* KPIs 3x2 */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi icon={Trophy} label="Municipios ganados" value={`${k.municipios_ganados}/${k.municipios_total}`} testid="kpi-munganados" />
        <Kpi icon={Zap} label="Switcher (riesgo)" value={k.municipios_switcher} testid="kpi-switcher" />
        <Kpi icon={Users} label="Visitas hoy" value={k.visitas_hoy} testid="kpi-visitas" />
        <Kpi icon={Heart} label="Compromisos SÍ" value={k.compromisos_si} testid="kpi-compromisos" />
        <Kpi icon={MapIcon} label="Cobertura válida" value={k.municipios_cobertura_valida} testid="kpi-cobertura" />
        <Kpi icon={Clock} label="Días a elección" value={k.dias_para_eleccion} testid="kpi-countdown" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Volatility table */}
        <div className="lg:col-span-2 noctua-card p-5">
          <h3 className="font-display font-bold text-lg text-white">Top 10 volatilidad</h3>
          <p className="text-xs text-slate-400 mt-0.5">Municipios con mayor riesgo (switcher + margen bajo)</p>
          {data.volatilidad_top10.length === 0 ? (
            <div className="text-slate-500 text-center py-8 text-sm">Sin datos suficientes</div>
          ) : (
            <table className="w-full mt-3 text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="text-left py-2">Municipio</th>
                  <th className="text-left py-2">Ganador</th>
                  <th className="text-right py-2">Margen</th>
                  <th className="text-right py-2">Cambios</th>
                  <th className="text-right py-2">Riesgo</th>
                </tr>
              </thead>
              <tbody>
                {data.volatilidad_top10.map((v) => (
                  <tr key={v.municipio_id} className="border-b border-slate-800/50" data-testid={`vol-row-${v.municipio_id}`}>
                    <td className="py-2 text-white">{v.nombre}</td>
                    <td className="py-2"><span className="px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: `${partyColor(v.ganador)}30`, color: partyColor(v.ganador) }}>{v.ganador}</span></td>
                    <td className="py-2 text-right tabular-nums">{v.margen_pts} pts</td>
                    <td className="py-2 text-right">{v.switches}</td>
                    <td className="py-2 text-right">
                      <span className="font-display font-bold tabular-nums text-red-400">{v.risk_score}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Alerts */}
        <div className="noctua-card p-5">
          <h3 className="font-display font-bold text-lg text-white flex items-center gap-2"><AlertCircle className="w-4 h-4 text-orange-400" /> Alertas Noctua</h3>
          <div className="mt-3 space-y-2 max-h-96 overflow-y-auto">
            {data.alertas.length === 0 ? (
              <div className="text-slate-500 text-sm text-center py-4">Sin alertas activas</div>
            ) : data.alertas.map((a, i) => (
              <div key={i} className={`p-3 rounded alert-${a.nivel}`} data-testid={`alert-${i}`}>
                <div className="font-medium text-white text-sm">{a.titulo}</div>
                <div className="text-xs text-slate-300 mt-0.5">{a.mensaje}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Party distribution */}
      <div className="noctua-card p-5">
        <h3 className="font-display font-bold text-lg text-white">Distribución por partido</h3>
        <div className="mt-4 space-y-2">
          {data.distribucion_partidos.length === 0 ? (
            <div className="text-slate-500 text-sm text-center py-6">Sin resultados cargados</div>
          ) : data.distribucion_partidos.map((p) => {
            const total = data.distribucion_partidos.reduce((s, x) => s + x.municipios, 0);
            const pct = total ? (p.municipios / total) * 100 : 0;
            return (
              <div key={p.partido} className="flex items-center gap-3 text-sm">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: partyColor(p.partido) }} />
                <span className="w-20 text-slate-300">{p.partido}</span>
                <div className="flex-1 h-3 bg-slate-800 rounded overflow-hidden">
                  <div className="h-full" style={{ width: `${pct}%`, backgroundColor: partyColor(p.partido) }} />
                </div>
                <span className="text-white tabular-nums w-20 text-right">{p.municipios} mun.</span>
                <span className="text-slate-400 tabular-nums w-16 text-right">{p.pct_promedio}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Executive report button */}
      <div className="flex items-center justify-between border-t border-slate-800 pt-5 mt-6 flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-blue-400">Reporte Ejecutivo</div>
          <div className="font-display font-bold text-white text-sm mt-0.5">Consolida KPIs · Top 10 volatilidad · Alertas Noctua en una página imprimible.</div>
        </div>
        <button
          onClick={() => printExecutiveReport(data, partyColor, scopeFilter)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition"
          data-testid="int-download-pdf"
        >
          <Download className="w-4 h-4" />
          Descargar reporte ejecutivo (PDF)
        </button>
      </div>
    </div>
  );
}

function printExecutiveReport(data, partyColor, scopeFilter) {
  const w = window.open("", "_blank");
  if (!w) return;
  const k = data.kpis;
  const now = new Date();
  const fecha = now.toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });
  const hora = now.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });

  const scopeLine = (() => {
    if (!scopeFilter) return "Vista nacional";
    const st = scopeFilter.scope_type;
    if (st === "estatal") return `Estatal · ${scopeFilter.entidad_nombre || ""}`;
    if (st === "municipal") return `Municipal · ${scopeFilter.municipio_nombre || ""}, ${scopeFilter.entidad_nombre || ""}`;
    if (st === "distrital_federal") return `Distrito Federal ${scopeFilter.distrito_clave} · ${scopeFilter.entidad_nombre || ""}`;
    if (st === "distrital_local") return `Distrito Local ${scopeFilter.distrito_clave} · ${scopeFilter.entidad_nombre || ""}`;
    return st || "";
  })();

  const kpiRows = [
    ["Municipios ganados", `${k.municipios_ganados} / ${k.municipios_total}`],
    ["Switcher (riesgo)", k.municipios_switcher],
    ["Visitas de hoy", k.visitas_hoy],
    ["Compromisos SÍ", k.compromisos_si],
    ["Cobertura válida", k.municipios_cobertura_valida],
    ["Días a la elección", k.dias_para_eleccion],
  ];

  const volRows = (data.volatilidad_top10 || []).map((v) => `
    <tr>
      <td>${v.nombre}</td>
      <td><span class="badge" style="background:${partyColor(v.ganador)}22;color:${partyColor(v.ganador)}">${v.ganador || "—"}</span></td>
      <td class="num">${v.margen_pts ?? "—"} pts</td>
      <td class="num">${v.switches ?? 0}</td>
      <td class="num risk">${v.risk_score ?? "—"}</td>
    </tr>`).join("");

  const alertRows = (data.alertas || []).map((a) => `
    <div class="alert alert-${a.nivel || "info"}">
      <div class="alert-title">${a.titulo}</div>
      <div class="alert-msg">${a.mensaje || ""}</div>
    </div>`).join("");

  const partyRows = (data.distribucion_partidos || []).map((p) => `
    <tr>
      <td><span class="dot" style="background:${partyColor(p.partido)}"></span>${p.partido}</td>
      <td class="num">${p.municipios}</td>
      <td class="num">${p.pct_promedio}%</td>
    </tr>`).join("");

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>Reporte Ejecutivo · Noctua 360</title>
<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; color: #0f172a; margin: 0; font-size: 12px; line-height: 1.5; }
  .header { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 3px solid #1e40af; padding-bottom: 12px; margin-bottom: 18px; }
  .brand { font-size: 10px; letter-spacing: 0.25em; text-transform: uppercase; color: #1e40af; font-weight: 700; }
  h1 { font-size: 22px; margin: 6px 0 2px 0; font-weight: 900; }
  .subtitle { color: #64748b; font-size: 11px; }
  .meta { text-align: right; font-size: 10px; color: #64748b; }
  .meta strong { color: #0f172a; }
  section { margin-top: 18px; }
  section h2 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.15em; color: #1e40af; border-bottom: 1px solid #e2e8f0; padding-bottom: 4px; margin: 0 0 10px 0; }
  .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .kpi { border: 1px solid #e2e8f0; border-radius: 4px; padding: 10px 12px; }
  .kpi-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.15em; color: #64748b; }
  .kpi-value { font-size: 20px; font-weight: 900; margin-top: 4px; color: #0f172a; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: 0.1em; color: #64748b; border-bottom: 1px solid #cbd5e1; padding: 6px 8px; }
  td { padding: 6px 8px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
  td.risk { color: #b91c1c; font-weight: 700; }
  .badge { display: inline-block; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: 700; }
  .dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
  .alert { border-left: 3px solid #f59e0b; background: #fffbeb; padding: 8px 10px; border-radius: 3px; margin-bottom: 6px; }
  .alert-title { font-weight: 700; font-size: 11px; color: #0f172a; }
  .alert-msg { font-size: 10.5px; color: #475569; margin-top: 1px; }
  .alert-alto, .alert-critico { border-left-color: #dc2626; background: #fef2f2; }
  .alert-medio { border-left-color: #f59e0b; background: #fffbeb; }
  .alert-bajo { border-left-color: #0891b2; background: #ecfeff; }
  .two-col { display: grid; grid-template-columns: 2fr 1fr; gap: 18px; }
  .footer { margin-top: 24px; border-top: 1px solid #e2e8f0; padding-top: 8px; font-size: 9px; color: #94a3b8; text-align: center; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style>
</head><body>
<div class="header">
  <div>
    <div class="brand">Noctua 360 · Inteligencia Electoral</div>
    <h1>Reporte Ejecutivo</h1>
    <div class="subtitle">${scopeLine} · Partido cliente: <strong>${data.partido_cliente || "—"}</strong></div>
  </div>
  <div class="meta">
    Generado el <strong>${fecha}</strong><br>a las ${hora}
  </div>
</div>

<section>
  <h2>Indicadores clave</h2>
  <div class="kpi-grid">
    ${kpiRows.map(([l, v]) => `
      <div class="kpi"><div class="kpi-label">${l}</div><div class="kpi-value">${v ?? "—"}</div></div>`).join("")}
  </div>
</section>

<section class="two-col">
  <div>
    <h2>Top 10 volatilidad</h2>
    ${volRows ? `<table>
      <thead><tr><th>Municipio</th><th>Ganador</th><th class="num">Margen</th><th class="num">Switches</th><th class="num">Riesgo</th></tr></thead>
      <tbody>${volRows}</tbody>
    </table>` : `<div style="color:#94a3b8">Sin datos suficientes.</div>`}
  </div>
  <div>
    <h2>Alertas Noctua</h2>
    ${alertRows || `<div style="color:#94a3b8">Sin alertas activas.</div>`}
  </div>
</section>

<section>
  <h2>Distribución por partido</h2>
  ${partyRows ? `<table>
    <thead><tr><th>Partido</th><th class="num">Municipios</th><th class="num">% promedio</th></tr></thead>
    <tbody>${partyRows}</tbody>
  </table>` : `<div style="color:#94a3b8">Sin resultados cargados.</div>`}
</section>

<div class="footer">
  Noctua 360 · Reporte generado automáticamente · Datos electorales oficiales integrados con analítica ML.
</div>

<script>window.onload=()=>{setTimeout(()=>window.print(),400)};</script>
</body></html>`;
  w.document.write(html);
  w.document.close();
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="noctua-kpi p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
        <Icon className="w-3.5 h-3.5 text-blue-400" />
      </div>
      <div className="mt-2 font-display font-black text-2xl text-white tabular-nums">{value}</div>
    </div>
  );
}
